import { useState, type FormEvent } from "react";
import { trackEvent } from "@/lib/analytics-events";

const ROLES = ["Executive Assistant", "Travel Manager", "Flight Department", "Charter or FBO", "Executive", "Other"];
const TRIPS = ["1–2", "3–5", "6+"];
const AIRPORTS = ["PDK", "FTY", "RYY", "LZU", "ATL"];

const input =
  "w-full rounded-sm border border-input bg-background px-3 py-2.5 text-sm text-foreground focus:outline-none focus:ring-2 focus:ring-ring";
const label = "block text-xs font-medium uppercase tracking-wide text-muted-foreground mb-1.5";

export default function CorporateAccountForm({ compact = false }: { compact?: boolean }) {
  const [status, setStatus] = useState<"idle" | "sending" | "done" | "error">("idle");
  const [airports, setAirports] = useState<string[]>([]);

  async function onSubmit(e: FormEvent<HTMLFormElement>) {
    e.preventDefault();
    const fd = new FormData(e.currentTarget);
    const role = String(fd.get("role") || "");
    const trips = String(fd.get("trips_per_month") || "");
    const travelCities = String(fd.get("travel_cities") || "").trim();
    setStatus("sending");
    try {
      const res = await fetch("/api/public/corporate-account", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          full_name: fd.get("full_name"),
          company: fd.get("company"),
          work_email: fd.get("work_email"),
          phone: fd.get("phone"),
          role,
          trips_per_month: trips,
          airports,
          travel_cities: travelCities,
          notes: fd.get("notes") || "",
          page_path: window.location.pathname,
        }),
      });
      if (!res.ok) throw new Error("failed");
      trackEvent("corporate_lead", {
        form_name: "corporate",
        role,
        trips_per_month: trips,
        has_travel_cities: travelCities.length > 0,
        page_path: window.location.pathname,
      });
      setStatus("done");
    } catch {
      setStatus("error");
    }
  }

  return (
    <section id="corporate-account" className={`bg-surface ${compact ? "py-14" : "py-16 sm:py-24"}`}>
      <div className={`mx-auto px-4 ${compact ? "max-w-2xl" : "max-w-3xl"}`}>
        <div className="text-center mb-8">
          <h2 className="font-heading text-2xl sm:text-4xl font-semibold mb-3">Set Up a Corporate Account</h2>
          <p className="text-muted-foreground text-sm sm:text-base">
            No setup fee, no commitments. One monthly invoice for every trip.
          </p>
        </div>

        {status === "done" ? (
          <div className="rounded-md border border-border bg-card p-8 text-center" role="status">
            <p className="font-heading text-xl">
              Thank you. We'll contact you within 2 business hours. If you shared your travel cities, we'll confirm our vetted partners there too.
            </p>
          </div>
        ) : (
          <form onSubmit={onSubmit} className="rounded-md border border-border bg-card p-5 sm:p-8 grid gap-4 sm:grid-cols-2">
            <div>
              <label className={label} htmlFor="ca-name">Full name *</label>
              <input id="ca-name" name="full_name" required maxLength={120} className={input} autoComplete="name" />
            </div>
            <div>
              <label className={label} htmlFor="ca-company">Company *</label>
              <input id="ca-company" name="company" required maxLength={160} className={input} autoComplete="organization" />
            </div>
            <div>
              <label className={label} htmlFor="ca-email">Work email *</label>
              <input id="ca-email" name="work_email" type="email" required maxLength={255} className={input} autoComplete="email" />
            </div>
            <div>
              <label className={label} htmlFor="ca-phone">Phone *</label>
              <input id="ca-phone" name="phone" type="tel" required minLength={7} maxLength={40} className={input} autoComplete="tel" />
            </div>
            <div>
              <label className={label} htmlFor="ca-role">Role</label>
              <select id="ca-role" name="role" className={input} defaultValue="">
                <option value="">Select…</option>
                {ROLES.map((r) => <option key={r} value={r}>{r}</option>)}
              </select>
            </div>
            <div>
              <label className={label} htmlFor="ca-trips">Typical trips per month</label>
              <select id="ca-trips" name="trips_per_month" className={input} defaultValue="">
                <option value="">Select…</option>
                {TRIPS.map((t) => <option key={t} value={t}>{t}</option>)}
              </select>
            </div>
            <fieldset className="sm:col-span-2">
              <legend className={label}>Atlanta airports you use</legend>
              <div className="flex flex-wrap gap-2">
                {AIRPORTS.map((a) => {
                  const on = airports.includes(a);
                  return (
                    <label
                      key={a}
                      className={`cursor-pointer rounded-sm border px-3 py-1.5 text-sm ${on ? "border-primary bg-primary text-primary-foreground" : "border-input"}`}
                    >
                      <input
                        type="checkbox"
                        className="sr-only"
                        checked={on}
                        onChange={() => setAirports((p) => (on ? p.filter((x) => x !== a) : [...p, a]))}
                      />
                      {a}
                    </label>
                  );
                })}
              </div>
            </fieldset>
            <div className="sm:col-span-2">
              <label className={label} htmlFor="ca-cities">Cities or countries you travel to most</label>
              <input id="ca-cities" name="travel_cities" maxLength={500} className={input} placeholder="e.g. Miami, New York, Los Angeles, London" />
            </div>
            <div className="sm:col-span-2">
              <label className={label} htmlFor="ca-notes">Notes</label>
              <textarea id="ca-notes" name="notes" rows={compact ? 3 : 4} maxLength={2000} className={input} />
            </div>
            <div className="sm:col-span-2">
              <button type="submit" disabled={status === "sending"} className="btn-primary rounded-sm w-full disabled:opacity-60">
                {status === "sending" ? "Sending…" : "Request Corporate Account"}
              </button>
              {status === "error" && (
                <p className="text-destructive text-sm mt-3 text-center">
                  Something went wrong. Please try again or call (678) 431-7222.
                </p>
              )}
            </div>
          </form>
        )}
      </div>
    </section>
  );
}
